import * as React from "react"
import { PiWarningLight } from "react-icons/pi"

import type { ButtonProps } from "@chakra-ui/react"
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  HStack,
  Icon,
  Text,
} from "@chakra-ui/react"

type Props = {
  title: React.ReactNode
  message?: React.ReactNode
  extraWarning?: boolean | null
  onConfirm: () => void
  isVisible: boolean
  setIsVisible: (value: boolean) => void
  confirmButtonProps?: ButtonProps
  cancelButtonProps?: ButtonProps
  /**
   * Set false for async confirms: the dialog stays open (drive the spinner via
   * confirmButtonProps.isLoading) and the caller closes it on completion.
   */
  closeOnConfirm?: boolean
}

export const ConfirmActionAlert = ({
  title,
  message,
  extraWarning,
  onConfirm,
  isVisible,
  setIsVisible,
  confirmButtonProps,
  cancelButtonProps,
  closeOnConfirm = true,
}: Props) => {
  const cancelRef = React.useRef(null)

  const onClose = () => {
    setIsVisible(false)
  }

  return (
    <AlertDialog
      isOpen={isVisible}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent textAlign="left">
          <AlertDialogHeader fontSize="lg" fontWeight={600}>
            {title}
          </AlertDialogHeader>

          <AlertDialogBody>
            <HStack spacing={4}>
              {extraWarning && <Icon as={PiWarningLight} fontSize={25} />}
              <Text>
                {message ||
                  "Are you sure? You can't undo this action afterwards."}
              </Text>
            </HStack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button
              id="cancel-button"
              colorScheme="tertiary"
              size="sm"
              ref={cancelRef}
              onClick={onClose}
              {...cancelButtonProps}
            >
              {cancelButtonProps?.children || "Cancel"}
            </Button>

            <Button
              id="confirm-button"
              colorScheme="red"
              size="sm"
              onClick={() => {
                if (closeOnConfirm) setIsVisible(false)
                onConfirm()
              }}
              ml={3}
              {...confirmButtonProps}
            >
              {confirmButtonProps?.children || "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
